import Button from '@/components/Button';
import { ButtonVariant } from '@/components/ButtonVariant';
import { Icon, IconVariant } from '@/components/Icon';
import classnames from '@/utilities/classnames';
import React from 'react';

export interface DropdownSelectProps {
  selected: string;
  prefix?: string;
  icon?: IconVariant;
  onChange: (value: string) => void;
  options: {
    [key: string]: string;
  };
}

interface DropdownSelectState {
  opened: boolean;
}

interface ClickEvent extends MouseEvent {
  target: Element;
}

export class DropdownSelect extends React.Component<
  DropdownSelectProps,
  DropdownSelectState
> {
  private trigger: HTMLElement;

  constructor(props) {
    super(props);
    this.state = { opened: false };
  }

  private get icon(): IconVariant {
    return this.props.icon || IconVariant.ChevronDown;
  }

  private get currentLabel(): string {
    return (
      (this.props.prefix || '') + this.props.options[this.props.selected] ||
      'Auswählen'
    );
  }

  private handleOnOptionClick = (value: string): void => {
    this.setState({ opened: false });
    this.props.onChange(value);
  };

  private handleOnTriggerClick = (): void => {
    this.setState((oldState) => ({ opened: !oldState.opened }));
  };

  private clickEventListener = ($event: ClickEvent): void => {
    const clicked = $event.target;
    const parent = clicked.parentElement;
    if (
      clicked &&
      clicked !== this.trigger &&
      parent !== this.trigger &&
      (parent ? parent.parentElement !== this.trigger : true)
    ) {
      this.setState({ opened: false });
    }
  };

  componentDidMount() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.addEventListener('click', this.clickEventListener);
    }
  }

  render(): JSX.Element {
    return (
      <div className="DropdownSelect">
        <Button
          onRef={(el) => (this.trigger = el)}
          variant={ButtonVariant.Primary}
          onClick={this.handleOnTriggerClick}
          className={classnames('DropdownSelect__trigger', {
            'DropdownSelect__trigger--active': this.state.opened,
          })}
        >
          <span>{this.currentLabel}</span>
          <Icon variant={this.icon} />
        </Button>
        <ul
          className={classnames('DropdownSelect__list', {
            'DropdownSelect__list--opened': this.state.opened,
          })}
        >
          {Object.keys(this.props.options).map((key, i) => (
            <li
              key={i}
              onClick={() => this.handleOnOptionClick(key)}
              className={classnames('DropdownSelect__option', {
                'DropdownSelect__option--selected': key === this.props.selected,
              })}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  this.handleOnOptionClick(key);
                }
              }}
            >
              {this.props.options[key]}
            </li>
          ))}
        </ul>
      </div>
    );
  }
}
